import { useState, type MouseEvent } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { ProcessingInsightDto } from '@/types/dtos/media/processing-insight.dto';
import type { MediaProcessingInstructionsContext } from '@/types/media-processing-instructions.types';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { hasShareableProcessingInstructions } from '@/helpers/media-processing-instructions.helper';
import { hasProcessingAdvisories } from '@/helpers/media-processing-policy-messages.helper';
import {
  buildProcessingInsightTooltip,
  processingInsightStatusLabelKey,
} from '@/helpers/media-processing-insight-tooltip.helper';
import { MediaProcessingInstructionsModal } from '@/components/media/media-processing-instructions.modal';

type MediaProcessingInsightBadgeProps = {
  insight?: ProcessingInsightDto;
  size?: 'small' | 'medium';
  instructionsContext?: MediaProcessingInstructionsContext;
};

function statusLabelKey(insight: ProcessingInsightDto): TranslationKey {
  return processingInsightStatusLabelKey(insight);
}

function chipColor(insight: ProcessingInsightDto): 'default' | 'info' | 'warning' | 'success' | 'error' {
  if (insight.readiness === 'rejected') {
    return 'error';
  }
  if (insight.readiness === 'failed') {
    return 'info';
  }
  if ((insight.readiness === 'ok' || insight.playoutReady) && hasProcessingAdvisories(insight)) {
    return 'warning';
  }
  if (insight.readiness === 'ok' || insight.playoutReady) {
    return 'success';
  }
  if (insight.status === 'running') {
    return 'info';
  }
  return 'info';
}

function chipIcon(insight: ProcessingInsightDto, iconProps?: SvgIconProps) {
  if (insight.readiness === 'rejected') {
    return <ErrorOutlineIcon {...iconProps} />;
  }
  if (insight.readiness === 'failed') {
    return <HourglassEmptyIcon {...iconProps} />;
  }
  if (insight.readiness === 'ok' || insight.playoutReady) {
    return <CheckCircleOutlineIcon {...iconProps} />;
  }
  return <HourglassEmptyIcon {...iconProps} />;
}

function iconColorSx(color: ReturnType<typeof chipColor>) {
  return color === 'default' ? { color: 'action.active' } : { color: `${color}.main` };
}

function buildTooltip(
  insight: ProcessingInsightDto,
  t: ReturnType<typeof useTranslations>,
  includeOpenHint: boolean,
): string {
  return buildProcessingInsightTooltip(insight, t, { includeInstructionsHint: includeOpenHint });
}

export function MediaProcessingInsightBadge({
  insight,
  size = 'small',
  instructionsContext,
}: MediaProcessingInsightBadgeProps) {
  const t = useTranslations();
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  if (!insight || insight.readiness === 'not_applicable') {
    return null;
  }

  const label = t(statusLabelKey(insight));
  const color = chipColor(insight);
  const canOpenInstructions = hasShareableProcessingInstructions(insight) && !!instructionsContext?.mediaTitle;
  const tooltip = buildTooltip(insight, t, canOpenInstructions);

  const handleBadgeClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (canOpenInstructions) {
      setInstructionsOpen(true);
    }
  };

  return (
    <>
      <Tooltip
        title={
          <Stack spacing={0.5}>
            {tooltip.split('\n').map((line, index) => (
              <Typography key={`${index}-${line}`} variant="caption" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                {line}
              </Typography>
            ))}
          </Stack>
        }
        placement="top-end"
        disableInteractive
      >
        <Box
          component="span"
          data-processing-insight-badge=""
          role={canOpenInstructions ? 'button' : undefined}
          tabIndex={canOpenInstructions ? 0 : undefined}
          aria-label={canOpenInstructions ? `${label}. ${t('media.processing.instructionsModal.openHint')}` : label}
          onClick={handleBadgeClick}
          onMouseDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (!canOpenInstructions) {
              return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              setInstructionsOpen(true);
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            verticalAlign: 'middle',
            cursor: canOpenInstructions ? 'pointer' : 'default',
          }}
        >
          <Box
            component="span"
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              alignItems: 'center',
              ...iconColorSx(color),
            }}
          >
            {chipIcon(insight, { fontSize: 'small' })}
          </Box>
          <Chip
            size={size}
            color={color}
            variant="outlined"
            icon={chipIcon(insight)}
            label={label}
            onClick={handleBadgeClick}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              maxWidth: '100%',
              ...(canOpenInstructions ? { cursor: 'pointer' } : {}),
            }}
          />
        </Box>
      </Tooltip>

      {canOpenInstructions && instructionsContext ? (
        <MediaProcessingInstructionsModal
          open={instructionsOpen}
          onClose={() => setInstructionsOpen(false)}
          insight={insight}
          context={instructionsContext}
        />
      ) : null}
    </>
  );
}
